/** FLD-OFF §2 — project warm-up, pin/evict, "cached as of". */
import { idb } from "./db";
import { beginCritical } from "./link";
import { drw, fetchSheetPdf } from "../../drawings/api";
import { cacheSheet } from "../../drawings/offlineCache";

const MAX_PROJECTS = 6;

/** DRW views the installer opens offline: index, unmatched spaces, stamps, then every current sheet + its PDF. */
async function cacheDrawings(projectId) {
  let urls = 3, ok = 0;
  const hit = async (fn) => { try { const r = await fn(); ok += 1; return r; } catch { return null; } };
  const CRIT = { _critical: true, _heavy: true };
  const index = await hit(() => drw.get(`/projects/${projectId}/sheets`, { params: { search: "" }, ...CRIT }));
  await hit(() => drw.get(`/projects/${projectId}/spaces`, { params: { unmatched: true }, ...CRIT }));
  await hit(() => drw.get(`/projects/${projectId}/stamps`, CRIT));
  for (const s of index?.data?.sheets || []) {
    urls += 2;
    const d = await hit(() => drw.get(`/sheets/${s.id}`, CRIT));
    const pdf = await hit(() => fetchSheetPdf(s.id));
    if (d && pdf) cacheSheet(s.id, pdf, d.data).catch(() => {});
  }
  return { urls, ok };
}

export async function cacheProject(api, projectId, urls) {
  // FLD-OFF-FIX-3 §2 — a warm-up is dozens of large reads. It is a server-dependent operation:
  // its own slowness, and any unrelated failure during it, must not put the app offline.
  const release = beginCritical(60000);
  try { return await runCacheProject(api, projectId, urls); } finally { release(); }
}

async function runCacheProject(api, projectId, urls) {
  const list = urls || (await api.get("/offline/projects", { _noCache: true })).data.urls;
  let ok = 0;
  for (const tpl of list) {
    const u = tpl.replace("{id}", projectId);
    // FLD-OFF-FIX-3 — the warm-up's OWN requests are the operation, so their real network
    // failures do count; `_heavy` keeps their slowness out of it.
    try { await api.get(u, { timeout: 20000, _critical: true, _heavy: true }); ok += 1; } catch { /* keep going — a partial cache beats none */ }
  }
  const d = await cacheDrawings(projectId);
  const meta = (await idb.get("meta", "projects")) || {};
  meta[projectId] = { ...(meta[projectId] || {}), cached_at: new Date().toISOString(), urls: list.length + d.urls, ok: ok + d.ok };
  await idb.set("meta", "projects", meta);
  await evict(api);
  return meta[projectId];
}

export async function projectsMeta() { return (await idb.get("meta", "projects")) || {}; }

export async function pinProject(projectId, pinned) {
  const meta = (await idb.get("meta", "projects")) || {};
  meta[projectId] = { ...(meta[projectId] || {}), pinned };
  await idb.set("meta", "projects", meta);
}

async function evict() {
  const meta = (await idb.get("meta", "projects")) || {};
  const loose = Object.entries(meta).filter(([, m]) => !m.pinned).sort((a, b) => (a[1].cached_at || "").localeCompare(b[1].cached_at || ""));
  while (Object.keys(meta).length > MAX_PROJECTS && loose.length) {
    const [pid] = loose.shift();
    for (const k of await idb.keys("api-cache")) if (String(k).includes(pid)) await idb.del("api-cache", k);
    delete meta[pid];
  }
  await idb.set("meta", "projects", meta);
}

export async function forgetProject(projectId) {
  for (const k of await idb.keys("api-cache")) if (String(k).includes(projectId)) await idb.del("api-cache", k);
  const meta = (await idb.get("meta", "projects")) || {};
  delete meta[projectId];
  await idb.set("meta", "projects", meta);
}

/** Auto-refresh every assigned project when online (called from the shell). */
export async function refreshAssigned(api) {
  const { data } = await api.get("/offline/projects", { _noCache: true });
  for (const p of data.projects) await cacheProject(api, p.id, data.urls);
  return data.projects;
}
